import { Fragment, useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Select,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import GoogleMap from '../map/GoogleMap';
import ShapeEditControl from '../map/ShapeEditControl';
import { useShapeVertexEditing } from '../../../hooks/useShapeVertexEditing';
import { presetContextOf } from '../../../algorithms/bathymetry/preset';
import { TRAIN_FRACTION } from '../../../algorithms/bathymetry/validation';
import { trainingCollection } from '../../../algorithms/bathymetry/depthImage';
import { plottedSample } from '../../../algorithms/bathymetry/trainingSamples';
import StepperButtons from '../acquisition/StepperButtons';
import * as Map from '../../../common/map';
import { aoiGeometry, aoiPoints, isMultiPolygon, parseKmlArea } from '../../../common/aoi';
import TourGuider from '../tour/TourGuider';
import { useLocalStorage } from '../../../common/utils';
import HelpButton from '../core/HelpButton';
import { usePreProcessing, useResult, useFile } from '../../contexts/index.jsx';
import { ToggleButtonGroup, ToggleButton } from '@material-ui/lab';
import { getSelectedSatellite, DATE_FORMATS } from '../../../algorithms/utils/index.js';
import ActivityIndicator from '../core/ActivityIndicator.jsx';
import { HelpOutline, CloudUpload } from '@material-ui/icons';
import SimpleInfoDialog from '../dialog/SimpleInfoDialog';
import { NEXT } from '../../../hooks/useStepNavigations';

/**
 * @function AOIChooser
 * @description This is the page of the second step of bathymetry's acquisition.
 * It allows the user to draw the Area of Interest (AOI) and to save it in the storage.
 * @param {Function} navigate - Function to navigate between steps in the acquisition process.
 * @param {Function} onSkipChange - function to update the skip status of this step in the parent component, allowing dynamic
 * skipping of steps based on user interactions or requirements.
 * **Called by:
 * @see {Bathymetry} - bathymetry's acquisition (index.jsx)
 * @memberof module:Bathymetry
 * @returns {JSX.Element} - renders Map, drawing tools along header and footer
 */
const DEFAULT_COEFFICIENT_VALUES = {
  1: {
    0: { c: '', a: '', b: '', d: '' },
  },
  2: {
    0: { m1: '', m0: '' },
  },
  3: {
    colorRed: { m1: '', m0: '' },
    colorGreen: { m1: '', m0: '' },
  },
};

const DEFAULT_TIDE_CONFIG = {
  enabled: false,
  datetimeField: 'datetime',
  levelField: 'total_water_level',
  datetimeFormat: 'yyyy-MM-dd HH:mm:ss',
  maxDiffMin: 90,
  compositeReducer: 'min',
};

const AOIChooser = ({ navigate, onSkipChange, sessionImportButton }) => {
  const [t] = useTranslation();
  const notifyError = useErrorNotice();
  const { data, setData } = useResult();
  const { fileData, setFileData, setUploadedFile } = useFile();
  const [overlay, setOverlay] = useState(null);
  const [coordinates, setCoordinates] = useState(() => fileData?.coordinates ?? null);
  const aoiEditing = useShapeVertexEditing({ onChange: setCoordinates });
  const [trainingData, setTrainingData] = useState(() => fileData?.trainingMatrix ?? []);
  const [waterMaskMethod, setWaterMaskMethod] = useState(() => fileData?.waterMaskMethod ?? 'NDWI');
  const [trainingDataReducer, setTrainingDataReducer] = useState(() => data?.trainingDataReducer ?? 'median');
  const [validationSplit, setValidationSplit] = useState(() => data?.validationSplit ?? false);
  const [bringingModel, setBringingModel] = useState(() => Boolean(data?.bringingModel));
  const [timezoneOffset, setTimezoneOffset] = useState(() => fileData?.timezoneOffset ?? 0);
  const { saveProcessingCollectionValues, waterMaskMethodRef } = usePreProcessing();
  const [threshold, setThreshold] = useState(() => fileData?.threshold ?? 0.124);
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState('');
  const satellite = useMemo(() => getSelectedSatellite(), []);
  const [trainingDate, setTrainingDate] = useState(() => fileData?.date ?? '');
  const [coordinatesSystem, setCoordinatesSystem] = useState(() => fileData?.coordinatesSystem ?? 'EPSG:4326');
  const [trainingFileName, setTrainingFileName] = useState(() => fileData?.fileName ?? '');
  const [kmlFileName, setKmlFileName] = useState(() => fileData?.kmlFileName ?? '');
  const [openDialog, setOpenDialog] = useState(null);
  const [modelPreset, setModelPreset] = useState(() => data?.modelPresetSelection ?? 1);
  const [coefficientValues, setCoefficientValues] = useState(
    () => data?.coefficientValuesRaw ?? DEFAULT_COEFFICIENT_VALUES,
  );
  const trainingCircles = useRef([]);
  const closeDialog = () => setOpenDialog(null);
  const usePresetMode = Object.values(coefficientValues[modelPreset]).every((group) =>
    Object.values(group).every((coef) => coef !== ''),
  );
  useEffect(() => {
    onSkipChange(usePresetMode, 3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usePresetMode]);
  const [tideFileName, setTideFileName] = useState(() => fileData?.tideFileName ?? '');
  const [tideHeaders, setTideHeaders] = useState([]);
  const [tideRawRows, setTideRawRows] = useState([]);
  const [tideDatetimeColumn, setTideDatetimeColumn] = useState('');
  const [tideLevelColumn, setTideLevelColumn] = useState('');
  const [tideConfig, setTideConfig] = useState(() => data?.tideConfig ?? DEFAULT_TIDE_CONFIG);
  const [tideWarningOpen, setTideWarningOpen] = useState(false);
  const tideInputRef = useRef(null);
  const autoContinueAfterTideImportRef = useRef(false);

  /**
   * @function saveZoomChange
   * @description Saves current map zoom to session storage.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const saveZoomChange = () => {
    const zoom = Map.getZoomLevel();
    if (zoom) window.sessionStorage.setItem('zoom_level', zoom);
  };

  useEffect(() => {
    window.sessionStorage.removeItem('zoom_level');
    Map.setDrawingControlsVisible(true);
    const intervalId = setInterval(saveZoomChange, 5000);
    return () => {
      clearInterval(intervalId);
      Map.onZoomChange(() => saveZoomChange());
    };
  }, []);

  /**
   * @function handleDrawing
   * @description handle drawing the AOI on the map and disable the drawing controls when the AOI is drawn.
   * 1. Saves the new overlay and coordinates to the component's state.
   * 2. Hides the drawing controls on the map to prevent further drawing until the user decides to draw again.
   * 3. If valid coordinates are provided, calculates the bounds of the drawn AOI and fits the map view to those bounds for better visualization.
   * @param {Object} newOverlay - The new overlay object representing the drawn AOI on the map.
   * @param {Array} newCoordinates - The coordinates of the drawn AOI.
   * **Called by:
   * @see {GoogleMap} - via the onRegionDrawn callback prop.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const handleDrawing = (newOverlay, newCoordinates, type) => {
    setOverlay(newOverlay);
    setCoordinates(newCoordinates);
    aoiEditing.setShape(newOverlay, type);
    Map.setDrawingControlsVisible(false);
    if (newCoordinates && newCoordinates.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      newCoordinates.forEach((c) => bounds.extend({ lat: c[1], lng: c[0] }));
      window.initMap.fitBounds(bounds, 0);
    }
  };

  /**
   * @function handleMapLoad
   * @description Runs once the Google Map finishes initializing. If `coordinates` was already
   * restored from `fileData` (i.e. the user navigated back to this step after having drawn/imported
   * an AOI before), redraws the polygon overlay and hides the drawing controls, mirroring what
   * `handleDrawing`/`readFileData` do right after a fresh draw/import. Also replots the training-data
   * circles from `fileData.matrixToPlot` when available, so both the AOI and training points reappear
   * on the map without the user having to redo them.
   * @see {@link handleDrawing} - equivalent redraw logic for a freshly-drawn AOI.
   * **Called by:
   * @see {GoogleMap} - via the onLoad prop, once on mount.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const handleMapLoad = () => {
    if (coordinates && coordinates.length > 0) {
      const openRing = Map.toOpenRing(coordinates);
      const restoredOverlay = Map.drawOutline(openRing);
      setOverlay(restoredOverlay);
      setCoordinates(openRing);
      aoiEditing.setShape(isMultiPolygon(openRing) ? null : restoredOverlay, 'polygon');
      Map.setDrawingControlsVisible(false);
      const bounds = new window.google.maps.LatLngBounds();
      aoiPoints(openRing).forEach((c) => bounds.extend({ lat: c[1], lng: c[0] }));
      window.initMap.fitBounds(bounds, 0);
    }
    if (fileData?.matrixToPlot?.length > 0) {
      plotTrainingData(fileData.matrixToPlot);
    }
  };

  /**
   * @function handleCoefChange
   * @description handle changing the coefficient values for PRESET mode.
   * @param {string} model - The name of the model for which the coefficients are being updated (e.g., 'Lyzenga', 'Stumpf 2003', 'Caballero & Stumpf 2020').
   * @param {string} group - The group of coefficients being updated (e.g., 'colorRed', 'colorGreen').
   * @param {string} label - The specific coefficient label being updated (e.g., 'm1', 'm0', 'c', 'a', 'b', 'd').
   * @param {string} value - The new value for the specified coefficient, which will be stored in the component's state.
   * **Called by:
   * @see {AOIChooser} - via the coefficient TextField's onChange, in preset mode.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const handleCoefChange = (model, group, label, value) => {
    const updatedGroup = { ...coefficientValues[model][group], [label]: value };
    setCoefficientValues((prev) => ({ ...prev, [model]: { ...prev[model], [group]: updatedGroup } }));
  };

  /**
   * @function handleUndo
   * @description Undoes the drawn/imported AOI and training data, resetting the form back to its
   * initial state and re-enabling the map's drawing controls.
   * 1. Removes the AOI overlay from the map and clears overlay/coordinates state.
   * 2. Clears the KML/CSV/tide file names and the plotted training-data circles.
   * 3. Resets the preset coefficient values, tide config, and tide column-selection state to their defaults.
   * 4. Re-enables the map's drawing controls so the user can draw a new AOI.
   * @see {@link clearTrainingData} - removes the plotted training-data circles from the map.
   * **Called by:
   * @see {AOIChooser} - via the "Undo" button's onClick.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const handleUndo = () => {
    aoiEditing.setShape(null);
    if (overlay) overlay.setMap(null);
    setOverlay(null);
    setCoordinates(null);
    setKmlFileName('');
    clearTrainingData();
    setTrainingData([]);
    setTrainingFileName('');
    setCoefficientValues({
      1: {
        0: { c: '', a: '', b: '', d: '' },
      },
      2: {
        0: { m1: '', m0: '' },
      },
      3: {
        colorRed: { m1: '', m0: '' },
        colorGreen: { m1: '', m0: '' },
      },
    });
    Map.setDrawingControlsVisible(true);
    setTideConfig({
      enabled: false,
      datetimeField: 'datetime',
      levelField: 'total_water_level',
      datetimeFormat: 'yyyy-MM-dd HH:mm:ss',
      maxDiffMin: 90,
      compositeReducer: 'min',
      applyCorrection: true,
    });
    setTideFileName('');
    setTideHeaders([]);
    setTideRawRows([]);
    setTideDatetimeColumn('');
    setTideLevelColumn('');
  };

  /**
   * @function performChoose
   * @description Processes the user-selected AOI by calculating cloud coverage, then dispatches the AOI data to
   * the application state. If the user lacks GEE API access, redirects to the signup page.
   * 1. Sets the loading state to true to indicate that processing is underway.
   * 2. Retrieves the selected satellite and its type (e.g., 'TOA' or 'SR') from session storage.
   * 3. Gets the first available mission and its start year based on the satellite and type.
   * 4. Calls the saveProcessingCollectionValues function to save the AOI and related parameters for later processing in the usePreProcessing context.
   * 5. Updates the waterMaskMethodRef with the selected water masking method for use in later processing steps.
   * 6. Prepares the data to be saved in the application state, including training data, preset coefficients if applicable, selected model, and satellite bands.
   * 7. Saves file-related data such as coordinate system, training date, and timezone offset in the application state for later use.
   * A new area drops what the period and algorithm steps kept for coming back to them, and a new survey drops the
   * algorithm step's: kept, the period step restored its period and went on with the collection of the area before -
   * a KML imported after a period had been chosen came out as a composite of the previous area.
   * 8. If any errors occur during processing (e.g., due to lack of GEE API access), catches the error, alerts the user, and redirects them to the GEE signup page.
   * 9. Finally, sets the loading state back to false to indicate that processing is complete, regardless of success or failure.
   * @see {@link saveProcessingCollectionValues} - saves the AOI and related parameters for later processing in the usePreProcessing context
   * @see {@link Acquisition.setAOI} - adds AOI data to the application state
   * **Called by:
   * @see {handleChoose} - directly, when tide correction doesn't require the warning gate.
   * @see {handleContinueWithoutTide} - after the user dismisses the tide warning.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const performChoose = async () => {
    setLoadingLabel(t('forms.core.loading.cloudCoverage'));
    setLoading(true);
    try {
      aoiEditing.stopEditing();
      const selectedSatellite = getSelectedSatellite();
      const type = window.sessionStorage.getItem('typeSatellite');
      const ms = type === 'TOA' ? selectedSatellite.missions[0] : selectedSatellite.surfaceReflectance[0];
      const startYear = ms.startYear;
      saveProcessingCollectionValues(
        selectedSatellite,
        `${startYear}-01-01`,
        aoiGeometry(coordinates),
        threshold,
      );
      waterMaskMethodRef.current = waterMaskMethod;
      const models = ['Lyzenga', 'Stumpf 2003', 'Caballero & Stumpf 2020'];
      setData((prev) => ({
        ...prev,
        trainingDataReducer,
        validationSplit,
        bringingModel,
        presetCoeficients: usePresetMode
          ? Object.fromEntries(
              Object.entries(coefficientValues[modelPreset]).map(([key, group]) => [
                key,
                Object.fromEntries(Object.entries(group).map(([key, value]) => [key, Number(value)])),
              ]),
            )
          : null,
        presetMode: usePresetMode,
        modelSelected: usePresetMode ? models[modelPreset - 1] : null,
        bands: usePresetMode ? [ms.bands.blue, ms.bands.green, ms.bands.red] : null,
        presetContext: usePresetMode
          ? presetContextOf(selectedSatellite, type, [ms.bands.blue, ms.bands.green, ms.bands.red])
          : null,
        tideConfig,
        modelPresetSelection: modelPreset,
        coefficientValuesRaw: coefficientValues,
      }));
      setFileData((prev) => ({
        ...prev,
        ...(prev?.coordinates !== coordinates && { periodState: undefined, bandsState: undefined }),
        ...(prev?.trainingMatrix !== trainingData && { bandsState: undefined }),
        coordinatesSystem: coordinatesSystem || 'EPSG:4326',
        date: trainingDate || null,
        timezoneOffset,
        coordinates,
        threshold,
        waterMaskMethod,
        kmlFileName,
        trainingMatrix: trainingData,
      }));
    } catch (e) {
      notifyError(t('forms.acquisition.2.bathymetryAoiChooser.geeSignupError'));
      window.location = 'https://earthengine.google.com/signup/';
    }

    setLoading(false);
  };

  /**
   * @function handleChoose
   * @description Gate before performChoose(): if tide correction is enabled but no tide CSV
   * has been imported yet, shows a warning modal instead of advancing, letting the user
   * either import the file or explicitly continue without it. Returning `false` here cancels
   * StepperButtons' navigation for this click (see StepperButtons.jsx's navigateForwards).
   * @see {@link performChoose} - performs the actual AOI/training-data processing.
   * **Called by:
   * @see {StepperButtons} - via the onNext prop.
   * @memberof module:Bathymetry
   * @returns {Promise<boolean|void>}
   */
  const handleChoose = async () => {
    if (tideConfig.enabled && !tideFileName) {
      setTideWarningOpen(true);
      return false;
    }
    return performChoose();
  };

  /**
   * @function handleContinueWithoutTide
   * @description Called from the tide-warning modal's "Continuar sem" button. Proceeds with
   * the AOI/training data as-is (tide correction left enabled in config but with no data to
   * apply it), then advances to the next step directly (bypassing StepperButtons, since this
   * runs outside of its onClick).
   * @see {@link performChoose} - performs the actual AOI/training-data processing.
   * **Called by:
   * @see {AOIChooser} - via the tide-warning Dialog's "Continuar sem" button onClick.
   * @memberof module:Bathymetry
   * @returns {Promise<void>}
   */
  const handleContinueWithoutTide = async () => {
    setTideWarningOpen(false);
    await performChoose();
    navigate(NEXT);
  };

  /**
   * @function handleImportTideClick
   * @description Called from the tide-warning modal's "Importar" button. Closes the modal and
   * opens the OS file picker for the tide CSV. Marks autoContinueAfterTideImportRef so that,
   * if the user closes the picker without choosing a file, the warning modal reopens instead of
   * silently leaving tide correction enabled with no data. Picking a file never auto-advances —
   * the user always reviews/selects the datetime and level columns before clicking "Next" themselves.
   * **Called by:
   * @see {AOIChooser} - via the tide-warning Dialog's "Importar" button onClick.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const handleImportTideClick = () => {
    setTideWarningOpen(false);
    autoContinueAfterTideImportRef.current = true;
    tideInputRef.current?.click();
  };

  /**
   * @function handleTideFilePickerCancel
   * @description Native 'cancel' event on the tide CSV <input type="file">, fired when the user
   * closes the OS file picker without choosing a file. Reopens the warning modal so the user
   * has to explicitly pick "Continuar sem" or "Importar" again.
   * **Called by:
   * @see {AOIChooser} - bound as a native 'cancel' event listener on the tide file input.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const handleTideFilePickerCancel = () => {
    if (!autoContinueAfterTideImportRef.current) return;
    autoContinueAfterTideImportRef.current = false;
    setTideWarningOpen(true);
  };

  useEffect(() => {
    const input = tideInputRef.current;
    if (!input) return;
    input.addEventListener('cancel', handleTideFilePickerCancel);
    return () => input.removeEventListener('cancel', handleTideFilePickerCancel);
  }, [tideConfig.enabled]);

  /**
   * @function readFileData
   * @description Reads a KML file, extracts coordinates, updates the map and hides drawing controls.
   * 1. Reads the selected KML file using a FileReader.
   * 2. Parses the file content to extract coordinates from the KML structure.
   * 3. If valid coordinates are found, creates a new overlay on the map to represent the AOI and centralizes the map view on the AOI.
   * 4. If the file cannot be interpreted as a valid KML with coordinates, alerts the user with an error message.
   * @param {Event} e - Input change event containing the selected file.
   * **Called by:
   * @see {AOIChooser} - via the KML file input's onChange.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const readFileData = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setKmlFileName(file.name);
    setLoadingLabel(t('forms.core.loading.kml'));
    setLoading(true);
    const reader = new FileReader();
    try {
      reader.onload = () => {
        try {
          const fileContent = reader.result;
          const isKml = fileContent.indexOf('<coordinates>') !== -1;
          if (!isKml) return notifyError(t('forms.csqueeze.2.errorMessageUnableInterpretKml'));
          const area = parseKmlArea(fileContent);
          if (!area) return notifyError(t('forms.csqueeze.2.errorMessageUnableInterpretKml'));
          if (overlay) overlay.setMap(null);
          const openRing = Map.toOpenRing(area);
          const overlayFixed = Map.drawOutline(openRing);
          setOverlay(overlayFixed);
          setCoordinates(openRing);
          aoiEditing.setShape(isMultiPolygon(openRing) ? null : overlayFixed, 'polygon');
          Map.fitToCoordinates(openRing);
          const bounds = new window.google.maps.LatLngBounds();
          aoiPoints(openRing).forEach((c) => bounds.extend({ lat: c[1], lng: c[0] }));
          window.initMap.fitBounds(bounds, 0);
          Map.setDrawingControlsVisible(false);
        } finally {
          setLoading(false);
        }
      };
      reader.readAsText(file);
    } catch (error) {
      setLoading(false);
      notifyError(t('forms.csqueeze.2.errorMessageUnableInterpretKml'));
    }
  };

  /**
   * @function plotTrainingData
   * @description Draws circles in the map for each coordinate in the Training Data
   * @param {Array<Array<number>>} matrix - List of [x, y] coordinate pairs.
   * @see {@link clearTrainingData} - removes training data points from the map by clearing the circles drawn to represent them.
   * **Called by:
   * @see {showPlotsInMap}
   * @memberof module:Bathymetry
   * @return {void}
   */
  const plotTrainingData = (matrix) => {
    const table = [];
    matrix.forEach((row) => table.push([row[0], row[1]]));
    const coordinatesArray = table;
    if (coordinatesArray.length === 0) return;
    clearTrainingData();
    const circles = Map.drawCircle(coordinatesArray);
    trainingCircles.current = circles ?? [];
    const lats = coordinatesArray.map((c) => c[1]);
    const lngs = coordinatesArray.map((c) => c[0]);
    const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
    Map.centralize(centerLat, centerLng);
    const bounds = new window.google.maps.LatLngBounds();
    coordinatesArray.forEach((c) => bounds.extend({ lat: c[1], lng: c[0] }));
    window.initMap.fitBounds(bounds, 40);
  };

  /**
   * @function clearTrainingData
   * @description Removes training data points from the map by clearing the circles drawn to represent them.
   * **Called by:
   * @see {plotTrainingData} - before redrawing the new set of circles.
   * @see {handleUndo}
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const clearTrainingData = () => {
    trainingCircles.current.forEach((circle) => circle.setMap(null));
    trainingCircles.current = [];
  };

  /**
   * @function thresholdHandler
   * @description Deals with changes in the threshold value, clamping it to the valid [-1, 1] range
   * and falling back to the default (0.124) when the input isn't a number.
   * @param {number|string} value - The new threshold value typed by the user.
   * **Called by:
   * @see {AOIChooser} - via the water mask threshold TextField's onChange.
   * @memberof module:Bathymetry
   * @return {void}
   */
  const thresholdHandler = (value) => {
    if (value < -1) return setThreshold(-1);
    if (value > 1) return setThreshold(1);
    setThreshold(isNaN(value) ? 0.124 : value);
  };

  /**
   * @function matrixToFeatureCol
   * @description Converts the input matrix of training data points into a FeatureCollection, taking into account the specified coordinate system.
   * @param {Array<Array<number>>} matrix - The matrix of training data points, where each point is represented as an array of [x, y, depth].
   * @param {string} coordinatesSystem - The coordinate system of the input matrix (e.g., 'EPSG:4326', 'EPSG:3857').
   * @see {@link trainingCollection} - builds the collection on the server, so a large survey fits in a request.
   * **Called by:
   * @see {showPlotsInMap}
   * @memberof module:Bathymetry
   * @returns {ee.FeatureCollection}
   */
  const matrixToFeatureCol = async (matrix, coordinatesSystem) => {
    if (!matrix || matrix.length === 0) return [];
    return trainingCollection(matrix, coordinatesSystem);
  };

  /**
   * @function matrixToMapPlot
   * @description  Transforms the FeatureCollection to EPSG:4326 for compatibility with the map plotting functions.
   * @param {ee.FeatureCollection} featureCollection - The FeatureCollection to transform.
   * @see {@link matrixToFeatureCol} - Converts the input matrix of training data points into a FeatureCollection, taking into account the specified coordinate system.
   * **Called by:
   * @see {showPlotsInMap}
   * @memberof module:Bathymetry
   * @returns {Promise<Array<Array<number>>>}
   */
  const matrixToMapPlot = async (featureCollection) => {
    if (!featureCollection) return [];
    const transformed = featureCollection.map((f) => f.transform('EPSG:4326'));
    const result = await new Promise((resolve, reject) =>
      transformed.evaluate((data, err) => (err ? reject(err) : resolve(data))),
    );
    return result.features.map((f) => [f.geometry.coordinates[0], f.geometry.coordinates[1], f.properties?.or_depth]);
  };

  /**
   * @function showPlotsInMap
   * @description Converts training data matrix to a format suitable for plotting on the map, handling coordinate system transformations as needed.
   * 1. Converts the input matrix of training data points into a FeatureCollection, taking into account the specified coordinate system.
   * 2. If the coordinate system is not already in EPSG:4326, transforms the FeatureCollection to EPSG:4326 for compatibility with the map plotting functions.
   * 3. Updates the file data in the application state with both the original FeatureCollection and the transformed matrix ready for plotting.
   * 4. Calls the plotTrainingData function to draw the training data points on the map using the transformed coordinates.
   * @param {Array<Array<number>>} matrix - The original matrix of training data points, where each point is represented as an array of [x, y, depth].
   * @param {string} coordSys - The coordinate system of the input matrix (e.g., 'EPSG:4326', 'EPSG:3857').
   * Reports its own failures rather than rejecting: both call sites invoke it without awaiting, so a
   * rejection here would surface as an unhandled one - see {@link useErrorNotice}.
   * @see {@link matrixToFeatureCol} - Converts the input matrix of training data points into a FeatureCollection, taking into account the specified coordinate system.
   * @see {@link matrixToMapPlot} - Transforms the FeatureCollection to EPSG:4326 for compatibility with the map plotting functions.
   * @see {@link plotTrainingData} - Draws the training data points on the map using the transformed coordinates.
   * @see {@link plottedSample} - thins a large survey to what the map can draw; the model still trains on all of it.
   * **Called by:
   * @see {readFileTrainingData}
   * @see {AOIChooser} - via the coordinate system TextField's onBlur.
   * @memberof module:Bathymetry
   * @return {void}
   */
  const showPlotsInMap = async (matrix, coordSys) => {
    try {
      const matrixFeatureCol = await matrixToFeatureCol(matrix, coordSys);
      const shown = plottedSample(matrix);
      const matrixToPlot =
        coordSys === 'EPSG:4326' ? shown : await matrixToMapPlot(await matrixToFeatureCol(shown, coordSys));
      setFileData((prev) => ({
        ...prev,
        matrix: matrixFeatureCol,
        matrixToPlot: matrixToPlot,
      }));
      plotTrainingData(matrixToPlot);
    } catch (error) {
      console.error('[AOIChooser] the training points could not be drawn:', error);
      notifyError(error, { fallback: t('forms.acquisition.2.bathymetryAoiChooser.trainingPlotError') });
    }
  };

  /**
   * @function readFileTrainingData
   * @description Reads a CSV training data file, parses numeric rows into a matrix and extracts a date from the filename when available.
   * Loads the points on the map after import.
   * @param {Event} event - Input change event containing the selected CSV file.
   * @see {@link showPlotsInMap} - Converts training data matrix to a format suitable for plotting on the map, handling coordinate system transformations as needed.
   * @example X,Y,Z
   * -48.57977361,-26.17064283,8.75
   * -48.62037859,-26.2146174,9.75
   * **Called by:
   * @see {AOIChooser} - via the training-data CSV input's onChange.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const readFileTrainingData = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploadedFile(file);
    setTrainingFileName(file.name);
    setLoadingLabel(t('forms.core.loading.trainingData'));
    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const content = e.target.result;
        let lines = content.split('\n');
        lines = lines.splice(1, lines.length);
        const matrix = [];
        lines.forEach((line) => {
          const columns = line.split(',');
          if (columns[0] !== '' && columns[0] !== null && !Number.isNaN(Number.parseFloat(columns[0]))) {
            const x = Number.parseFloat(columns[0]);
            const y = Number.parseFloat(columns[1]);
            const z = Number.parseFloat(columns[2]);
            matrix.push([x, y, z]);
          }
        });
        let parsedTrainingDate = '';
        const fileName = file.name;
        const posUnderscore = fileName.lastIndexOf('_');
        const posDot = fileName.lastIndexOf('.');
        if (posDot - posUnderscore >= 6) {
          const day = fileName.substring(posUnderscore + 1, posUnderscore + 3);
          const month = fileName.substring(posUnderscore + 3, posUnderscore + 5);
          const year = fileName.substring(posUnderscore + 5, posUnderscore + 9);
          parsedTrainingDate = `${year}-${month}-${day}`;
          setTrainingDate(parsedTrainingDate);
        }
        let parsedCoordinatesSystem = coordinatesSystem;
        if (fileName.includes('EPSG')) {
          let i = fileName.indexOf('EPSG') + 4;
          let coordID = '';
          while (!isNaN(fileName[i]) && fileName[i] !== '') {
            coordID += fileName[i];
            i++;
          }
          parsedCoordinatesSystem = coordID ? `EPSG:${coordID}` : coordinatesSystem;
          if (parsedCoordinatesSystem !== coordinatesSystem) setCoordinatesSystem(parsedCoordinatesSystem);
        }
        setTrainingData(matrix);
        setFileData((prev) => ({
          ...prev,
          content,
          fileName: file.name,
          date: parsedTrainingDate,
          coordinatesSystem: parsedCoordinatesSystem,
        }));
        await showPlotsInMap(matrix, parsedCoordinatesSystem);
      } catch (error) {
        console.error('Error importing training data:', error);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(file);
  };

  /**
   * @function buildTideRows
   * @description Builds an array of [datetime, value] pairs from the parsed tide CSV rows, given which columns hold the datetime and the tide level.
   * @param {Array<string>} headers - Column headers parsed from the CSV.
   * @param {Array<Array<string>>} rows - Data rows parsed from the CSV, each an array of column values.
   * @param {string} datetimeCol - Header name of the column holding the datetime.
   * @param {string} levelCol - Header name of the column holding the tide level.
   * **Called by:
   * @see {readTideData} - once on auto-detected columns.
   * @see {AOIChooser} - via the column-selection effect, whenever the user changes a dropdown.
   * @memberof module:Bathymetry
   * @returns {Array<Array<string|number>>}
   */
  const buildTideRows = (headers, rows, datetimeCol, levelCol) => {
    const datetimeIdx = headers.indexOf(datetimeCol);
    const levelIdx = headers.indexOf(levelCol);
    if (datetimeIdx === -1 || levelIdx === -1) return [];
    const tideRows = [];
    rows.forEach((cols) => {
      const datetime = cols[datetimeIdx];
      const value = parseFloat((cols[levelIdx] || '').replace(',', '.'));
      if (!datetime || isNaN(value)) return;
      tideRows.push([datetime, value]);
    });
    return tideRows;
  };

  /**
   * @function readTideData
   * @description Reads a CSV tide data file and parses its header and rows. If a column named "datetime" or "tideData"
   * is found (case/formatting-insensitive), it is auto-selected as a starting point; either way the column dropdowns
   * are always shown once the headers are known, so the user can review or override the selection before continuing
   * with the normal "Next" click (import never auto-advances to the next step).
   * The function handles various edge cases such as different delimiters (comma or semicolon) and potential formatting issues in the CSV file.
   * If the file cannot be parsed correctly, it alerts the user with an error message.
   * A file was actually picked here (not canceled), so autoContinueAfterTideImportRef is cleared
   * right away, ensuring the warning modal doesn't reopen if a stray native 'cancel' event fires afterwards.
   * @param {Event} event - Input change event containing the selected tide CSV file.
   * @see {@link buildTideRows} - builds the [datetime, value] pairs once the columns are known (auto-detected or user-selected).
   * @example Datetime,Value
   * 2024-01-01T00:00:00,2.5
   * 2024-01-01T01:00:00,2.7
   * **Called by:
   * @see {AOIChooser} - via the tide CSV input's onChange.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const readTideData = (event) => {
    if (!event || !event.target || !event.target.files) return console.warn('Evento inválido no readTideData');
    const file = event.target.files[0];
    if (!file) return console.warn('Nenhum arquivo selecionado');
    autoContinueAfterTideImportRef.current = false;
    setTideFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        if (!e?.target?.result) throw new Error('Arquivo vazio');
        let content = e.target.result;
        content = content.replace(/^\uFEFF/, '');
        const lines = content
          .split('\n')
          .map((l) => l.replace('\r', '').trim())
          .filter(Boolean);
        if (lines.length < 2) throw new Error('Arquivo sem dados suficientes');
        const delimiter = lines[0].split(';').length > lines[0].split(',').length ? ';' : ',';
        const headers = lines[0].split(delimiter).map((h) => h.trim());
        const rows = lines.slice(1).map((line) => line.split(delimiter).map((c) => c.trim()));
        if (!rows.length) throw new Error('Nenhuma linha válida encontrada');
        const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const autoDatetimeCol = headers.find((h) => normalize(h) === 'datetime') || '';
        const autoLevelCol = headers.find((h) => normalize(h) === 'tidedata') || '';
        setTideHeaders(headers);
        setTideRawRows(rows);
        setTideDatetimeColumn(autoDatetimeCol);
        setTideLevelColumn(autoLevelCol);
        if (autoDatetimeCol && autoLevelCol) {
          const tideRows = buildTideRows(headers, rows, autoDatetimeCol, autoLevelCol);
          if (!tideRows.length) throw new Error('Nenhuma linha válida encontrada');
          setFileData((prev) => ({
            ...prev,
            tideData: tideRows,
            tideFileName: file.name,
          }));
        }
      } catch (err) {
        console.error('Erro ao ler CSV de maré:', err);
        notifyError(t('forms.acquisition.2.bathymetryAoiChooser.invalidTideFile'));
      }
    };
    reader.onerror = () => {
      console.error('Erro ao ler arquivo');
      notifyError(t('forms.acquisition.2.bathymetryAoiChooser.invalidTideFile'));
    };

    reader.readAsText(file);
  };

  useEffect(() => {
    if (!tideHeaders.length || !tideDatetimeColumn || !tideLevelColumn) return;
    const tideRows = buildTideRows(tideHeaders, tideRawRows, tideDatetimeColumn, tideLevelColumn);
    if (!tideRows.length) {
      console.warn('Nenhuma linha válida encontrada para as colunas selecionadas');
      return;
    }
    setFileData((prev) => ({
      ...prev,
      tideData: tideRows,
      tideFileName,
    }));
  }, [tideHeaders, tideRawRows, tideDatetimeColumn, tideLevelColumn]);

  const steps = [
    {
      selector: '#areachooser',
      content: t('tour.acquisition.2.info'),
    },
    {
      selector: '#importkml',
      content: t('tour.csqueeze.2.importKml'),
    },
    {
      selector: '#datatraining',
      content: t('tour.acquisition.2.dataTraining'),
    },
    {
      selector: '#trainingdatareducer',
      content:
        t('forms.acquisition.2.bathymetry.trainingDataReducerHelp') ||
        t('forms.acquisition.2.bathymetryAoiChooser.trainingDataReducerFallback'),
    },
  ];

  const [isTourOpen, setIsTourOpen] = useLocalStorage('showROITour', true);

  useEffect(() => {
    document.body.style.overflow = isTourOpen ? 'hidden' : 'auto';
  }, [isTourOpen]);

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ padding: '0 24px' }}>
        <HelpButton onClickFunction={() => setIsTourOpen(true)} extra={sessionImportButton} />
      </Box>
      <Paper
        id="aoi-chooser-form"
        elevation={1}
        style={{
          width: '100%',
          maxWidth: 1100,
          margin: '0 auto 24px auto',
          padding: '0 24px 24px 24px',
          borderRadius: 12,
        }}>
        <Box
          style={{
            width: '100%',
            maxWidth: 1100,
            margin: '0 0 12px 0',
            padding: '0 24px 12px 12px',
            boxSizing: 'border-box',
          }}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
            {t('forms.acquisition.2.bathymetryAoiChooser.inputFilesAndSettings')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('forms.acquisition.2.bathymetryAoiChooser.inputFilesAndSettingsDescription')}
          </Typography>
        </Box>
        <Box
          display="grid"
          style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 16,
            marginBottom: 24,
          }}>
          <Paper variant="outlined" id="importkml" style={{ padding: 16, borderRadius: 10 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 10 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {t('forms.acquisition.2.bathymetryAoiChooser.aoiFileTitle')}
              </Typography>
              <Typography color="error" style={{ fontWeight: 800, fontSize: 18 }}>
                *
              </Typography>
            </Box>
            <Button variant="outlined" color="primary" component="label" startIcon={<CloudUpload />}>
              {t('forms.acquisition.2.bathymetryAoiChooser.importKml')}
              <input
                hidden
                accept=".kml"
                type="file"
                onClick={(e) => {
                  e.target.value = null;
                }}
                onChange={readFileData}
              />
            </Button>
            <Typography variant="body2" style={{ marginTop: 10 }}>
              {kmlFileName ? (
                <>
                  {t('forms.acquisition.2.bathymetryAoiChooser.fileLabel')} <strong>{kmlFileName}</strong>
                </>
              ) : (
                t('forms.acquisition.2.bathymetryAoiChooser.noKmlFileSelected')
              )}
            </Typography>
            <Typography variant="body2" style={{ marginTop: 6 }}>
              {coordinates
                ? t('forms.acquisition.2.bathymetryAoiChooser.aoiLoadedOnMap')
                : t('forms.acquisition.2.bathymetryAoiChooser.drawOnMapOrImportKml')}
            </Typography>
          </Paper>
        </Box>
        <Paper
          variant="outlined"
          style={{
            borderRadius: 12,
            overflow: 'hidden',
            marginBottom: 16,
          }}>
          <div id={'areachooser'}>
            <GoogleMap style={{ width: '100%', height: '56vh' }} onRegionDrawn={handleDrawing} onLoad={handleMapLoad} />
          </div>
          <ShapeEditControl editing={aoiEditing} visible={Boolean(coordinates)} />
        </Paper>
        <Box
          display="grid"
          style={{
            gridTemplateColumns: '1fr 0.1fr 2fr',
            gap: 16,
            marginBottom: 24,
          }}>
          <Paper variant="outlined" id="datatraining" style={{ padding: 16, borderRadius: 10 }}>
            <Box display="flex" alignItems="center" gridGap={8} style={{ marginBottom: 10 }}>
              <HelpOutline
                onClick={() => setOpenDialog('help_training_file')}
                className="MuiSvgIcon-root MuiSvgIcon-colorPrimary MuiSvgIcon-fontSizeInherit"
                style={{ cursor: 'pointer' }}
              />
            </Box>
            <Box display="flex" alignItems="center" gridGap={8} style={{ marginBottom: 12 }}>
              <Button variant="outlined" color="primary" component="label" startIcon={<CloudUpload />}>
                {t('forms.acquisition.2.bathymetryAoiChooser.importCsv')}
                <input hidden accept=".csv" type="file" onChange={readFileTrainingData} />
              </Button>
            </Box>
            <Typography variant="body2">
              {trainingFileName ? (
                <>
                  {t('forms.acquisition.2.bathymetryAoiChooser.fileLabel')} <strong>{trainingFileName}</strong>
                </>
              ) : (
                t('forms.acquisition.2.bathymetryAoiChooser.noCsvFileSelected')
              )}
            </Typography>
            <Typography variant="body2" style={{ marginTop: 6 }}>
              {t('forms.acquisition.2.bathymetryAoiChooser.pointsLabel')} <strong>{trainingData?.length || ''}</strong>
            </Typography>
          </Paper>
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography variant="body2" color="textSecondary">
              {t('forms.acquisition.2.bathymetryAoiChooser.or')}
            </Typography>
          </Box>
          <Paper variant="outlined" style={{ padding: 16, borderRadius: 10 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 10 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {t('forms.acquisition.2.bathymetryAoiChooser.coeficientsTitle')}
              </Typography>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                PRESET MODE: <span style={{ color: '#42a5f5' }}>{usePresetMode ? 'ON' : 'OFF'}</span>
              </Typography>
            </Box>
            <Box variant="outlined" display="flex" alignItems="center" gridGap={8} style={{ marginBottom: 10 }}>
              <Box variant="outlined" display="flex" flexDirection="column" gridGap={8} style={{ marginBottom: 10 }}>
                <Select
                  value={modelPreset}
                  label={modelPreset}
                  onChange={(e) => setModelPreset(e.target.value)}
                  style={{ marginRight: 16 }}>
                  <MenuItem value={1}>Modelo 1 : Lyzenga </MenuItem>
                  <MenuItem value={2}>Modelo 2 : Stumpf</MenuItem>
                  <MenuItem value={3}>Modelo 3 : Stumpf&Caballero</MenuItem>
                </Select>
                {coefficientValues &&
                  Object.entries(coefficientValues[modelPreset] ?? {}).map(([group, labels]) => (
                    <Fragment key={group}>
                      {isNaN(group) && (
                        <Typography variant="subtitle2" style={{ fontWeight: 600, marginRight: 8 }}>
                          {t('color.' + String(group))}
                        </Typography>
                      )}
                      <Box
                        variant="outlined"
                        display="flex"
                        alignItems="center"
                        key={group}
                        gridGap={8}
                        style={{ marginBottom: 10 }}>
                        {Object.entries(labels).map(([label, value]) => (
                          <TextField
                            key={`${group}-${label}`}
                            type="number"
                            label={label}
                            value={value ?? ''}
                            onChange={(e) => handleCoefChange(modelPreset, group, label, e.target.value)}
                            inputProps={{ step: '0.01' }}
                            placeholder="0"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      </Box>
                    </Fragment>
                  ))}
              </Box>
            </Box>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              {t('forms.acquisition.2.bathymetryAoiChooser.coeficientsDescription')}
            </Typography>
          </Paper>
        </Box>
        <Box
          display="grid"
          style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 16,
            marginBottom: 24,
          }}>
          <Paper variant="outlined" style={{ padding: 16, borderRadius: 10 }}>
            <Box display="flex" alignItems="center" gridGap={8} style={{ marginBottom: 10 }}>
              <HelpOutline
                onClick={() => setOpenDialog('help_training_file')}
                className="MuiSvgIcon-root MuiSvgIcon-colorPrimary MuiSvgIcon-fontSizeInherit"
                style={{ cursor: 'pointer' }}
              />
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {t('forms.acquisition.2.bathymetryAoiChooser.trainingDateTitle')}
              </Typography>
            </Box>
            <TextField
              fullWidth
              type="date"
              value={trainingDate}
              onChange={(e) => setTrainingDate(e.target.value)}
              variant="outlined"
              size="small"
            />
          </Paper>
          <Paper variant="outlined" style={{ padding: 16, borderRadius: 10 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 10 }}>
              <Box display="flex" alignItems="center" gridGap={8} style={{ marginBottom: 10 }}>
                <HelpOutline
                  onClick={() => setOpenDialog('help_coordinate_system')}
                  className="MuiSvgIcon-root MuiSvgIcon-colorPrimary MuiSvgIcon-fontSizeInherit"
                  style={{ cursor: 'pointer' }}
                />
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  {t('forms.acquisition.2.bathymetryAoiChooser.coordinateSystemTitle')}
                </Typography>
              </Box>
              <Typography color="error" style={{ fontWeight: 800, fontSize: 18 }}>
                *
              </Typography>
            </Box>
            <TextField
              fullWidth
              value={coordinatesSystem}
              onChange={(e) => setCoordinatesSystem(e.target.value)}
              onBlur={(e) => {
                if (trainingData.length > 0) showPlotsInMap(trainingData, e.target.value);
              }}
              variant="outlined"
              size="small"
              placeholder={t('forms.acquisition.2.bathymetryAoiChooser.coordinateSystemExample')}
            />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              {t('forms.acquisition.2.bathymetryAoiChooser.coordinateSystemExample')}
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              {t('forms.acquisition.2.bathymetryAoiChooser.coordinateSystemLookup')}{' '}
              <a href="https://epsg.io" target="_blank" rel="noreferrer">
                epsg.io
              </a>
            </Typography>
          </Paper>

          <Paper variant="outlined" style={{ padding: 16, borderRadius: 10 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 10 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 10 }}>
                {t('forms.acquisition.2.bathymetry.waterMaskThreshold')}
              </Typography>
              <Typography color="error" style={{ fontWeight: 800, fontSize: 18 }}>
                *
              </Typography>
            </Box>
            <TextField
              type="number"
              value={threshold}
              onChange={(e) => thresholdHandler(e.target.value)}
              placeholder={t('forms.acquisition.2.bathymetryAoiChooser.thresholdPlaceholder')}
              variant="outlined"
              size="small"
              inputProps={{ step: '0.001' }}
            />
          </Paper>

          {satellite.name !== 'Landsat' && (
            <Paper variant="outlined" style={{ padding: 16, borderRadius: 10 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 10 }}>
                {t('forms.acquisition.2.bathymetry.waterMaskMethod')}
              </Typography>
              <ToggleButtonGroup
                value={waterMaskMethod}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue) setWaterMaskMethod(newValue);
                }}
                color="primary">
                <ToggleButton value={'NDWI'}>NDWI</ToggleButton>
                <ToggleButton value={'DSWE'} disabled={satellite.name === 'Landsat'}>
                  DSWE
                </ToggleButton>
              </ToggleButtonGroup>
            </Paper>
          )}

          <Paper variant="outlined" style={{ padding: 16, borderRadius: 10 }}>
            <Typography id="trainingdatareducer" variant="subtitle2" style={{ fontWeight: 600, marginBottom: 10 }}>
              {t('forms.acquisition.2.bathymetry.trainingDataReducer') ||
                t('forms.acquisition.2.bathymetryAoiChooser.trainingDataReducerFallback')}
            </Typography>
            <ToggleButtonGroup
              value={trainingDataReducer}
              exclusive
              onChange={(e, newValue) => {
                if (newValue) setTrainingDataReducer(newValue);
              }}
              color="primary">
              <ToggleButton value={'min'}>Min</ToggleButton>
              <ToggleButton value={'max'}>Max</ToggleButton>
              <ToggleButton value={'mean'}>Mean</ToggleButton>
              <ToggleButton value={'median'}>Median</ToggleButton>
            </ToggleButtonGroup>
            <Divider style={{ margin: '16px 0 12px' }} />
            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 12 }}>
              <Box minWidth={0}>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  {t('forms.acquisition.2.bathymetry.validationSplit')}
                </Typography>
                <Typography variant="caption" color="textSecondary" component="p">
                  {t('forms.acquisition.2.bathymetry.validationSplitHelp', {
                    train: Math.round(TRAIN_FRACTION * 100),
                    validation: Math.round((1 - TRAIN_FRACTION) * 100),
                  })}
                </Typography>
              </Box>
              <ToggleButtonGroup
                value={validationSplit ? 'on' : 'off'}
                exclusive
                onChange={(_, value) => {
                  if (value) setValidationSplit(value === 'on');
                }}
                size="small">
                <ToggleButton value="on">ON</ToggleButton>
                <ToggleButton value="off">OFF</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Paper>
        </Box>

        <Box style={{ marginBottom: 24 }}>
          <Paper variant="outlined" style={{ padding: 16, borderRadius: 10 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box style={{ paddingRight: 16 }}>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  {t('forms.acquisition.2.bathymetry.bringingModel')}
                </Typography>
                <Typography variant="caption" color="textSecondary" component="p">
                  {t('forms.acquisition.2.bathymetry.bringingModelHelp')}
                </Typography>
              </Box>
              <ToggleButtonGroup
                value={bringingModel ? 'on' : 'off'}
                exclusive
                onChange={(_, value) => {
                  if (value) setBringingModel(value === 'on');
                }}
                size="small">
                <ToggleButton value="on">ON</ToggleButton>
                <ToggleButton value="off">OFF</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Paper>
        </Box>

        <Box style={{ marginBottom: 24 }}>
          <Paper variant="outlined" style={{ padding: 16, borderRadius: 10 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 10 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {t('forms.acquisition.2.bathymetryAoiChooser.tideTitle') || 'Tide correction'}
              </Typography>
              <ToggleButtonGroup
                value={tideConfig.enabled ? 'on' : 'off'}
                exclusive
                onChange={(_, v) => {
                  if (v) setTideConfig((prev) => ({ ...prev, enabled: v === 'on' }));
                }}
                size="small">
                <ToggleButton value="on">ON</ToggleButton>
                <ToggleButton value="off">OFF</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {tideConfig.enabled && (
              <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
                <Paper
                  variant="outlined"
                  style={{
                    padding: 12,
                    borderRadius: 8,
                    marginBottom: 12,
                  }}>
                  <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                    {t('forms.acquisition.2.bathymetryAoiChooser.tideCsvFile') || 'Tide CSV'}
                  </Typography>

                  <Button variant="outlined" color="primary" component="label" startIcon={<CloudUpload />}>
                    {t('forms.acquisition.2.bathymetryAoiChooser.importCsv') || 'Import CSV'}
                    <input ref={tideInputRef} hidden accept=".csv" type="file" onChange={readTideData} />
                  </Button>

                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    {tideFileName
                      ? `Arquivo: ${tideFileName}`
                      : t('forms.acquisition.2.bathymetryAoiChooser.noCsvFileSelected')}
                  </Typography>

                  {tideHeaders.length > 0 && (
                    <Box display="flex" style={{ gap: 8, marginTop: 12 }}>
                      <TextField
                        select
                        label={t('forms.acquisition.2.bathymetryAoiChooser.tideDatetimeField') || 'Datetime field'}
                        value={tideDatetimeColumn}
                        onChange={(e) => setTideDatetimeColumn(e.target.value)}
                        variant="outlined"
                        size="small"
                        style={{ flex: 1 }}>
                        {tideHeaders.map((header) => (
                          <MenuItem key={header} value={header}>
                            {header}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label={t('forms.acquisition.2.bathymetryAoiChooser.tideLevelField') || 'Level field'}
                        value={tideLevelColumn}
                        onChange={(e) => setTideLevelColumn(e.target.value)}
                        variant="outlined"
                        size="small"
                        style={{ flex: 1 }}>
                        {tideHeaders.map((header) => (
                          <MenuItem key={header} value={header}>
                            {header}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  )}
                </Paper>
                <Box display="flex" style={{ gap: 8 }}>
                  <TextField
                    select
                    label={t('forms.acquisition.2.bathymetryAoiChooser.tideDatetimeFormat') || 'Datetime format'}
                    value={tideConfig.datetimeFormat}
                    onChange={(e) =>
                      setTideConfig((prev) => ({
                        ...prev,
                        datetimeFormat: e.target.value,
                      }))
                    }
                    variant="outlined"
                    size="small"
                    style={{ flex: 2 }}>
                    {DATE_FORMATS.map((format) => (
                      <MenuItem key={format.value} value={format.value}>
                        {format.value} ({format.label})
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    label={t('forms.acquisition.2.bathymetryAoiChooser.tideMaxDiff') || 'Max diff (min)'}
                    type="number"
                    value={tideConfig.maxDiffMin}
                    onChange={(e) =>
                      setTideConfig((prev) => ({
                        ...prev,
                        maxDiffMin: Number(e.target.value),
                      }))
                    }
                    variant="outlined"
                    size="small"
                    style={{ flex: 1 }}
                    inputProps={{ min: 1, step: 1 }}
                  />
                  <TextField
                    select
                    label={t('forms.acquisition.2.bathymetryAoiChooser.timezoneOffset') || 'Timezone offset (h)'}
                    value={timezoneOffset}
                    onChange={(e) => setTimezoneOffset(Number(e.target.value))}
                    variant="outlined"
                    size="small"
                    style={{ flex: 1 }}>
                    {Array.from({ length: 25 }, (_, i) => i - 12).map((offset) => (
                      <MenuItem key={offset} value={offset}>
                        {`UTC${offset >= 0 ? '+' : ''}${offset}`}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box display="flex" flexWrap="wrap" style={{ gap: 16 }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 4 }}>
                      {t('forms.acquisition.2.bathymetryAoiChooser.tideCompositeReducer') || 'Composite reducer'}
                    </Typography>
                    <ToggleButtonGroup
                      value={tideConfig.compositeReducer}
                      exclusive
                      onChange={(_, v) => {
                        if (v) setTideConfig((prev) => ({ ...prev, compositeReducer: v }));
                      }}
                      size="small">
                      {['min', 'max', 'mean', 'median'].map((r) => (
                        <ToggleButton key={r} value={r}>
                          {r}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                </Box>

                <Typography variant="caption" color="textSecondary">
                  {t('forms.acquisition.2.bathymetryAoiChooser.tideHint') ||
                    'Matching and chart will be shown in the next step after images are loaded.'}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Paper>
      <div align="center" id="downloadExamples" style={{ padding: '0 0 16px 0' }}>
        <div>
          {t('forms.acquisition.2.example')}:&nbsp;
          <br />
          Babitonga:{' '}
          <a href={'/babitonga.kml'.toString()}>
            {t('forms.acquisition.2.bathymetryAoiChooser.areaOfInterestExample')}
          </a>
          &nbsp;&nbsp;|&nbsp;&nbsp;
          <a href={'/batimetria_1K_EPSG4326positivo_20052018.csv'.toString()}>
            {t('forms.acquisition.2.bathymetryAoiChooser.trainingDataFile4326')}
          </a>
          &nbsp;&nbsp;|&nbsp;&nbsp;
          <a href={'/batimetria_1K_EPSG32722_20052018.csv'.toString()}>
            {t('forms.acquisition.2.bathymetryAoiChooser.trainingDataFile32722')}
          </a>
        </div>
      </div>
      <StepperButtons
        navigate={navigate}
        nextDisabled={
          aoiEditing.geometryError !== '' ||
          !((trainingData?.length > 0 || usePresetMode || bringingModel) && coordinates)
        }
        onNext={handleChoose}>
        <Button
          onClick={handleUndo}
          disabled={!((trainingData?.length > 0 || usePresetMode || bringingModel) && coordinates)}
          color="secondary"
          variant="outlined">
          {t('forms.acquisition.2.undo')}
        </Button>
      </StepperButtons>
      {loading && <ActivityIndicator forceShow={true} title={loadingLabel || t('forms.core.loading.area')} />}
      <TourGuider steps={steps} isOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
      <SimpleInfoDialog
        open={openDialog === 'help_coordinate_system'}
        close={closeDialog}
        title="forms.acquisition.2.helpCoordinateSystemTitle"
        text="forms.acquisition.2.helpCoordinateSystem"
      />
      <SimpleInfoDialog
        open={openDialog === 'help_date_training'}
        close={closeDialog}
        title="forms.acquisition.2.dataTrainningDateHintTitle"
        text="forms.acquisition.2.dataTrainningDateHint"
      />
      <SimpleInfoDialog
        open={openDialog === 'help_training_file'}
        close={closeDialog}
        title="forms.acquisition.2.bathymetryAoiChooser.helpTrainingFileTitle"
        texts={[
          'forms.acquisition.2.bathymetryAoiChooser.helpTrainingFileFormat',
          'forms.acquisition.2.bathymetryAoiChooser.helpTrainingFileColumns',
          'forms.acquisition.2.bathymetryAoiChooser.helpTrainingFileOrder',
          'forms.acquisition.2.bathymetryAoiChooser.helpTrainingFileDecimal',
        ]}
      />
      <Dialog open={tideWarningOpen} maxWidth="xs" fullWidth>
        <DialogTitle>{t('forms.acquisition.2.bathymetryAoiChooser.tideWarningTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('forms.acquisition.2.bathymetryAoiChooser.tideWarningText')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleContinueWithoutTide} color="secondary">
            {t('forms.acquisition.2.bathymetryAoiChooser.tideWarningContinueWithout')}
          </Button>
          <Button onClick={handleImportTideClick} color="primary" variant="contained">
            {t('forms.acquisition.2.bathymetryAoiChooser.tideWarningImport')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AOIChooser;
